import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  X,
  Sparkles,
  AlertCircle,
  Image as ImageIcon,
  Trash2,
  Bug,
  Lightbulb,
} from "lucide-react";
import { IssueCategory, IssuePriority, UserProfile, Issue } from "../types";
import { createIssue, CreateIssueInput } from "../services/issueService";
import { uploadToQuarantine } from "../services/storageService";
import { detectSourceApp } from "../utils/appDetection";
import { fetchClientIp } from "../utils/telemetry";

export interface CreateIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile;
  initialAppId?: string;
  initialAppName?: string;
  onIssueCreated?: (issue: Issue) => void;
}

export const CreateIssueModal: React.FC<CreateIssueModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  initialAppId,
  initialAppName,
  onIssueCreated,
}) => {
  const detectedApp = useMemo(
    () =>
      detectSourceApp({
        appId: initialAppId,
        appName: initialAppName,
      }),
    [initialAppId, initialAppName],
  );

  const [title, setTitle] = useState<string>("");
  const [category, setCategory] = useState<IssueCategory>("bug");
  const [priority, setPriority] = useState<IssuePriority>("medium");
  const [description, setDescription] = useState<string>("");
  const [expectedBehavior, setExpectedBehavior] = useState<string>("");
  const [url, setUrl] = useState<string>("");
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [clientIp, setClientIp] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (isOpen) {
      void fetchClientIp(1500).then((ip) => {
        if (isMounted && ip) {
          setClientIp(ip);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (screenshotPreview) {
        URL.revokeObjectURL(screenshotPreview);
      }
    };
  }, [screenshotPreview]);

  if (!isOpen) return null;

  const handleFileProcess = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setFormError(
        "Only image files (PNG, JPG, WebP, GIF) are supported for screenshots.",
      );
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setFormError("Screenshot exceeds the 25MB file size limit.");
      return;
    }

    setFormError(null);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }
    const previewUrl = URL.createObjectURL(file);
    setScreenshotPreview(previewUrl);
    setScreenshotFile(file);
  };

  const handleRemoveScreenshot = () => {
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }
    setScreenshotPreview(null);
    setScreenshotFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          const namedFile = file.name
            ? file
            : new File([file], `screenshot_${Date.now()}.png`, {
                type: file.type,
              });
          handleFileProcess(namedFile);
          break;
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!title.trim()) {
      setFormError("Please enter an issue title.");
      return;
    }
    if (!description.trim()) {
      setFormError(
        category === "bug"
          ? "Please provide issue details or reproduction steps."
          : "Please provide your feedback or suggestion.",
      );
      return;
    }
    if (category === "bug" && !expectedBehavior.trim()) {
      setFormError("Please enter what should have happened.");
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      let attachmentPaths: string[] = [];
      if (screenshotFile) {
        setIsUploading(true);
        const uploadResult = await uploadToQuarantine(
          currentUser.uid,
          screenshotFile,
        );
        attachmentPaths = [uploadResult.fullPath];
      }

      const input: CreateIssueInput = {
        appId: detectedApp.id,
        appName: detectedApp.name,
        title: title.trim(),
        category,
        priority,
        description: description.trim(),
        expectedBehavior:
          category === "bug" && expectedBehavior.trim()
            ? expectedBehavior.trim()
            : undefined,
        url: url.trim() || undefined,
        attachmentPaths,
        ipAddress: clientIp,
      };

      const created = await createIssue(input, currentUser);

      // Clean up form state to avoid accidental duplicate submissions
      setTitle("");
      setDescription("");
      setExpectedBehavior("");
      setUrl("");
      setScreenshotFile(null);
      if (screenshotPreview) {
        URL.revokeObjectURL(screenshotPreview);
        setScreenshotPreview(null);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (onIssueCreated) {
        onIssueCreated(created);
      }
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to create issue";
      setFormError(msg);
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-issue-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
    >
      <div className="glass max-w-lg w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border border-slate-200 bg-white">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/75 sticky top-0 z-10 backdrop-blur-md">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-teal-600 text-white flex items-center justify-center shadow-sm">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <h2
                id="create-issue-title"
                className="text-base font-bold text-slate-900"
              >
                {detectedApp.name}: Submit Issue or Feedback
              </h2>
              <p className="text-[11px] text-slate-500">
                Log a defect or feature request across MLC applications
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Form */}
        <form
          onSubmit={handleSubmit}
          onPaste={handlePaste}
          className="p-6 space-y-4"
        >
          {formError && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          {/* Top Segmented Selector: Bug vs Feedback */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Report Type
            </label>
            <div
              role="radiogroup"
              aria-label="Submission Type"
              className="grid grid-cols-2 p-1 bg-slate-100/90 rounded-xl border border-slate-200 shadow-2xs gap-1"
            >
              <button
                type="button"
                role="radio"
                aria-checked={category === "bug"}
                onClick={() => setCategory("bug")}
                className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  category === "bug"
                    ? "bg-white text-rose-700 shadow-xs border border-rose-200"
                    : "text-slate-600 hover:text-slate-900 border border-transparent"
                }`}
              >
                <Bug
                  className={`w-4 h-4 ${
                    category === "bug" ? "text-rose-600" : "text-slate-400"
                  }`}
                />
                <span>Bug Report</span>
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={category === "feature_request"}
                onClick={() => setCategory("feature_request")}
                className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  category === "feature_request"
                    ? "bg-white text-teal-700 shadow-xs border border-teal-200"
                    : "text-slate-600 hover:text-slate-900 border border-transparent"
                }`}
              >
                <Lightbulb
                  className={`w-4 h-4 ${
                    category === "feature_request"
                      ? "text-teal-600"
                      : "text-slate-400"
                  }`}
                />
                <span>General Feedback</span>
              </button>
            </div>
          </div>

          {/* Issue Title */}
          <div>
            <label
              htmlFor="issue-title"
              className="block text-xs font-semibold text-slate-700 mb-1"
            >
              {category === "bug" ? "Issue Title" : "Feedback Title"}
            </label>
            <input
              id="issue-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                category === "bug"
                  ? "Brief summary of the defect..."
                  : "Brief summary of your suggestion or feedback..."
              }
              className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-sm"
              maxLength={200}
              autoFocus
            />
          </div>

          {/* Priority */}
          <div>
            <label
              id="priority-label"
              className="block text-xs font-semibold text-slate-700 mb-1.5"
            >
              Priority
            </label>
            <div
              role="radiogroup"
              aria-labelledby="priority-label"
              className="grid grid-cols-4 gap-2"
            >
              {(
                [
                  {
                    value: "low",
                    label: "Low",
                    activeClasses:
                      "bg-emerald-50 text-emerald-800 border-emerald-400 ring-2 ring-emerald-500/20 font-semibold shadow-xs",
                    dotColor: "bg-emerald-500",
                  },
                  {
                    value: "medium",
                    label: "Medium",
                    activeClasses:
                      "bg-sky-50 text-sky-800 border-sky-400 ring-2 ring-sky-500/20 font-semibold shadow-xs",
                    dotColor: "bg-sky-500",
                  },
                  {
                    value: "high",
                    label: "High",
                    activeClasses:
                      "bg-amber-50 text-amber-800 border-amber-400 ring-2 ring-amber-500/20 font-semibold shadow-xs",
                    dotColor: "bg-amber-500",
                  },
                  {
                    value: "critical",
                    label: "Critical",
                    activeClasses:
                      "bg-rose-50 text-rose-800 border-rose-400 ring-2 ring-rose-500/20 font-bold shadow-xs",
                    dotColor: "bg-rose-500",
                  },
                ] as const
              ).map((opt) => {
                const isSelected = priority === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setPriority(opt.value)}
                    className={`flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-lg border text-xs transition-all cursor-pointer ${
                      isSelected
                        ? opt.activeClasses
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${opt.dotColor}`}
                      aria-hidden="true"
                    />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dynamic Description & Steps based on Category */}
          {category === "bug" ? (
            <>
              <div>
                <label
                  htmlFor="issue-description"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Description & Steps to Reproduce
                </label>
                <textarea
                  id="issue-description"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What happened? List the steps to reproduce the defect..."
                  maxLength={10000}
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="issue-expected-behavior"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  What should have happened? (Expected Behavior)
                </label>
                <textarea
                  id="issue-expected-behavior"
                  rows={3}
                  value={expectedBehavior}
                  onChange={(e) => setExpectedBehavior(e.target.value)}
                  placeholder="Describe what you expected to occur instead..."
                  maxLength={5000}
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-sm"
                />
              </div>
            </>
          ) : (
            <div>
              <label
                htmlFor="issue-description"
                className="block text-xs font-semibold text-slate-700 mb-1"
              >
                Feedback & Suggestions
              </label>
              <textarea
                id="issue-description"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Share your idea, suggestions, or desired improvement..."
                maxLength={10000}
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-sm"
              />
            </div>
          )}

          {/* Screenshot Upload */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label
                htmlFor="screenshot-upload"
                className="block text-xs font-semibold text-slate-700"
              >
                Screenshot (Optional)
              </label>
              <span className="text-[10px] text-slate-400">
                Paste (Ctrl+V) or click to upload
              </span>
            </div>

            <input
              id="screenshot-upload"
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileProcess(file);
              }}
              className="hidden"
            />

            {screenshotPreview && screenshotFile ? (
              <div className="flex items-center justify-between p-2.5 rounded-lg border border-teal-200 bg-teal-50/50">
                <div className="flex items-center gap-3 overflow-hidden">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="w-12 h-12 object-cover rounded-md border border-slate-200 shadow-2xs shrink-0"
                  />
                  <div className="overflow-hidden">
                    <p className="text-xs font-medium text-slate-800 truncate">
                      {screenshotFile.name}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      {(screenshotFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveScreenshot}
                  className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                  aria-label="Remove screenshot"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 hover:border-teal-500 rounded-lg text-slate-500 hover:text-teal-700 bg-slate-50/50 hover:bg-teal-50/20 transition-all cursor-pointer group"
              >
                <ImageIcon className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                <span className="text-xs font-medium">
                  Attach or drag screenshot here
                </span>
              </button>
            )}
          </div>

          {/* Context URL */}
          <div>
            <label
              htmlFor="issue-url"
              className="block text-xs font-semibold text-slate-700 mb-1"
            >
              Affected Page / URL (Optional)
            </label>
            <input
              id="issue-url"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://mlc-ops-hub.web.app/..."
              className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-sm"
            />
          </div>

          {/* Modal Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-animate inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-xs font-semibold shadow-md shadow-teal-600/20 hover:bg-teal-700 disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              <span>
                {isUploading
                  ? "Uploading Screenshot..."
                  : isSubmitting
                    ? "Submitting..."
                    : "Submit Ticket"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
